/**
 * Thin wrappers around the /api/v1/crm/* endpoints. All calls go through
 * `fetcher` which handles JWT injection + error extraction.
 */

import { fetcher } from '#lib/backendFetcher';

// Pipelines

/**
 * Fetches all pipelines of the organization.
 * @returns {Promise<Object>} List of all pipelines
 */
export const listPipelines = () => fetcher('/api/v1/crm/pipelines');

/**
 * Creates a new pipeline.
 * @param {Object} payload - The pipeline data
 * @returns {Promise<Object>} The created pipeline
 */
export const createPipeline = (payload) => fetcher('/api/v1/crm/pipelines', { method: 'POST', body: payload });

/**
 * Updates an existing pipeline.
 * @param {string} id - The pipeline ID
 * @param {Object} payload - The fields to update
 * @returns {Promise<Object>} The updated pipeline
 */
export const updatePipeline = (id, payload) =>
	fetcher(`/api/v1/crm/pipelines/${id}`, { method: 'PATCH', body: payload });

/**
 * Deletes a pipeline (soft delete).
 * @param {string} id - The pipeline ID
 * @returns {Promise<null>} No content on success
 */
export const deletePipeline = (id) => fetcher(`/api/v1/crm/pipelines/${id}`, { method: 'DELETE' });

/**
 * Restores a soft-deleted pipeline.
 * @param {string} id - The pipeline ID
 * @returns {Promise<Object>} The restored pipeline
 */
export const restorePipeline = (id) => fetcher(`/api/v1/crm/pipelines/${id}/restore`, { method: 'POST' });

/**
 * Fetches all soft-deleted pipelines (trash).
 * @param {Object} [filters={}] - Optional filter parameters
 * @returns {Promise<Object>} List of soft-deleted pipelines
 */
export const listPipelineTrash = (filters = {}) => {
	const qs = new URLSearchParams(
		Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== ''),
	).toString();
	return fetcher(`/api/v1/crm/pipelines/trash${qs ? `?${qs}` : ''}`);
};

/**
 * Creates a new stage within a pipeline.
 * @param {string} pipelineId - The pipeline ID
 * @param {Object} payload - The stage data
 * @returns {Promise<Object>} The created stage
 */
export const createStage = (pipelineId, payload) =>
	fetcher(`/api/v1/crm/pipelines/${pipelineId}/stages`, { method: 'POST', body: payload });

/**
 * Updates a stage within a pipeline.
 * @param {string} pipelineId - The pipeline ID
 * @param {string} stageId - The stage ID
 * @param {Object} payload - The fields to update
 * @returns {Promise<Object>} The updated stage
 */
export const updateStage = (pipelineId, stageId, payload) =>
	fetcher(`/api/v1/crm/pipelines/${pipelineId}/stages/${stageId}`, { method: 'PATCH', body: payload });

/**
 * Deletes a stage from a pipeline.
 * @param {string} pipelineId - The pipeline ID
 * @param {string} stageId - The stage ID
 * @returns {Promise<null>} No content on success
 */
export const deleteStage = (pipelineId, stageId) =>
	fetcher(`/api/v1/crm/pipelines/${pipelineId}/stages/${stageId}`, { method: 'DELETE' });

/**
 * Reorders stages within a pipeline.
 * @param {string} pipelineId - The pipeline ID
 * @param {Object[]} stageOrders - Array of stage IDs and their new positions
 * @returns {Promise<Object>} The updated order
 */
export const reorderStages = (pipelineId, stageOrders) =>
	fetcher(`/api/v1/crm/pipelines/${pipelineId}/stages/reorder`, { method: 'POST', body: { stageOrders } });

// Deals

/**
 * Fetches all deals with optional filters.
 * @param {Object} [filters={}] - Optional filter parameters (e.g. pipelineId, stageId, ownerId)
 * @returns {Promise<Object>} List of deals
 */
export const listDeals = (filters = {}) => {
	const qs = new URLSearchParams(
		Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== ''),
	).toString();
	return fetcher(`/api/v1/crm/deals${qs ? `?${qs}` : ''}`);
};

/**
 * Fetches summaries per stage (deal count, total value).
 * @param {Object} [filters={}] - Optional filter parameters
 * @returns {Promise<Object>} Stage summaries
 */
export const listStageSummaries = (filters = {}) => {
	const qs = new URLSearchParams(
		Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== ''),
	).toString();
	return fetcher(`/api/v1/crm/deals/stage-summaries${qs ? `?${qs}` : ''}`);
};

/**
 * Fetches a single deal by its ID.
 * @param {string} id - The deal ID
 * @returns {Promise<Object>} The deal with all details
 */
export const getDeal = (id) => fetcher(`/api/v1/crm/deals/${id}`);

/**
 * Creates a new deal.
 * @param {Object} payload - The deal data
 * @returns {Promise<Object>} The created deal
 */
export const createDeal = (payload) => fetcher('/api/v1/crm/deals', { method: 'POST', body: payload });

/**
 * Updates an existing deal.
 * @param {string} id - The deal ID
 * @param {Object} payload - The fields to update
 * @returns {Promise<Object>} The updated deal
 */
export const updateDeal = (id, payload) => fetcher(`/api/v1/crm/deals/${id}`, { method: 'PATCH', body: payload });

/**
 * Deletes a deal (soft delete).
 * @param {string} id - The deal ID
 * @returns {Promise<null>} No content on success
 */
export const deleteDeal = (id) => fetcher(`/api/v1/crm/deals/${id}`, { method: 'DELETE' });

/**
 * Restores a soft-deleted deal.
 * @param {string} id - The deal ID
 * @returns {Promise<Object>} The restored deal
 */
export const restoreDeal = (id) => fetcher(`/api/v1/crm/deals/${id}/restore`, { method: 'POST' });

/**
 * Fetches all soft-deleted deals (trash).
 * @param {Object} [filters={}] - Optional filter parameters
 * @returns {Promise<Object>} List of soft-deleted deals
 */
export const listDealTrash = (filters = {}) => {
	const qs = new URLSearchParams(
		Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== ''),
	).toString();
	return fetcher(`/api/v1/crm/deals/trash${qs ? `?${qs}` : ''}`);
};

/**
 * Soft-deletes several deals in one request (admin/owner only).
 *
 * @param {{ids: string[]}|{selectAll: true, filters?: Object}} scope - `filters` uses the deal list's
 *   query-param shape (userIds/tagIds as comma strings, pipelineId included)
 * @returns {Promise<{deleted: number}>}
 */
export const bulkDeleteDeals = (scope) => fetcher('/api/v1/crm/deals/bulk-delete', { method: 'POST', body: scope });

/**
 * Restores several soft-deleted deals in one request (admin/owner only).
 *
 * @param {{ids: string[]}|{selectAll: true, filters?: {search?: string}}} scope
 * @returns {Promise<{restored: number}>}
 */
export const bulkRestoreDeals = (scope) => fetcher('/api/v1/crm/deals/trash/restore', { method: 'POST', body: scope });

/**
 * Moves a deal to a different stage.
 * @param {string} id - The deal ID
 * @param {Object} payload - The target stage data (stageId, optional note)
 * @returns {Promise<Object>} The updated deal
 */
export const transitionStage = (id, payload) =>
	fetcher(`/api/v1/crm/deals/${id}/transition-stage`, { method: 'POST', body: payload });

/**
 * Recalculates the score of a deal.
 * @param {string} id - The deal ID
 * @returns {Promise<Object>} The deal with the updated score
 */
export const rescoreDeal = (id) => fetcher(`/api/v1/crm/deals/${id}/rescore`, { method: 'POST' });

// Activities

/**
 * Fetches all activities with optional filters.
 * @param {Object} [filters={}] - Optional filter parameters (e.g. dealId, type, status)
 * @returns {Promise<Object>} List of activities
 */
export const listActivities = (filters = {}) => {
	const qs = new URLSearchParams(
		Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== ''),
	).toString();
	return fetcher(`/api/v1/crm/activities${qs ? `?${qs}` : ''}`);
};

/**
 * Fetches a single activity by its ID.
 * @param {string} id - The activity ID
 * @returns {Promise<Object>} The activity with all details
 */
export const getActivity = (id) => fetcher(`/api/v1/crm/activities/${id}`);

/**
 * Fetches activity statistics with optional filters.
 * @param {Object} [filters={}] - Optional filter parameters
 * @returns {Promise<Object>} Statistical analysis of activities
 */
export const getActivityStats = (filters = {}) => {
	const qs = new URLSearchParams(
		Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== ''),
	).toString();
	return fetcher(`/api/v1/crm/activities/stats${qs ? `?${qs}` : ''}`);
};

/**
 * Creates a new activity.
 * @param {Object} payload - The activity data
 * @returns {Promise<Object>} The created activity
 */
export const createActivity = (payload) => fetcher('/api/v1/crm/activities', { method: 'POST', body: payload });

/**
 * Hydrates an email template for a concrete CRM recipient — the editable base shown in the
 * composer before scheduling.
 * @param {Object} payload
 * @param {string} payload.emailTemplateId
 * @param {string} [payload.crmContactId]
 * @param {string} [payload.crmCompanyId]
 * @returns {Promise<{emailSubject: string, emailContent: string, emailRecipient: string}>}
 */
export const previewActivityEmail = (payload) =>
	fetcher('/api/v1/crm/activities/email-preview', { method: 'POST', body: payload });

/**
 * Fetches the scheduled email snapshot behind an automated activity (edit prefill).
 * @param {string} activityId
 * @returns {Promise<{emailSend: Object}>}
 */
export const getActivityEmail = (activityId) => fetcher(`/api/v1/crm/activities/${activityId}/email`);

/**
 * Bulk-creates one activity per deal from a shared template.
 *
 * @param {Object} payload
 * @param {string[]} [payload.dealIds] - XOR `selectAll`
 * @param {boolean} [payload.selectAll] - Every deal matching `payload.filters` (deal list query-param shape)
 * @param {Object} [payload.filters]
 * @param {string} payload.activityType
 * @param {string} payload.title
 * @param {string} [payload.body]
 * @param {string} [payload.channelTypeId]
 * @param {string} [payload.customActivityLabel]
 * @param {'planned'|'completed'} [payload.status]
 * @param {string} [payload.dueAt]
 * @param {Object} [payload.channelPayload]
 * @returns {Promise<{createdCount: number, skippedCount: number}>}
 */
export const bulkCreateActivities = (payload) =>
	fetcher('/api/v1/crm/activities/bulk', { method: 'POST', body: payload });

/**
 * Bulk-creates one company-bound activity per company from a shared template.
 *
 * @param {Object} payload
 * @param {string[]} [payload.companyIds] - XOR `selectAll`
 * @param {boolean} [payload.selectAll] - Every company matching `payload.filters`
 * @param {{search?: string, tagIds?: string[]}} [payload.filters]
 * @param {string} payload.activityType
 * @param {string} payload.title
 * @param {string} [payload.body]
 * @param {string} [payload.channelTypeId]
 * @param {string} [payload.customActivityLabel]
 * @param {'planned'|'completed'} [payload.status]
 * @param {string} [payload.dueAt]
 * @param {Object} [payload.channelPayload]
 * @returns {Promise<{createdCount: number, skippedCount: number}>}
 */
export const bulkCreateActivitiesByCompanies = (payload) =>
	fetcher('/api/v1/crm/activities/bulk-by-companies', { method: 'POST', body: payload });

/**
 * Updates an existing activity.
 * @param {string} id - The activity ID
 * @param {Object} payload - The fields to update
 * @returns {Promise<Object>} The updated activity
 */
export const updateActivity = (id, payload) =>
	fetcher(`/api/v1/crm/activities/${id}`, { method: 'PATCH', body: payload });

/**
 * Marks an activity as completed.
 * @param {string} id - The activity ID
 * @param {Object} payload - Completion data (e.g. result, notes)
 * @returns {Promise<Object>} The completed activity
 */
export const completeActivity = (id, payload) =>
	fetcher(`/api/v1/crm/activities/${id}/complete`, { method: 'POST', body: payload });

/**
 * Deletes an activity.
 * @param {string} id - The activity ID
 * @returns {Promise<null>} No content on success
 */
export const deleteActivity = (id) => fetcher(`/api/v1/crm/activities/${id}`, { method: 'DELETE' });

// Contacts

/**
 * Fetches all contacts of a CRM company.
 * @param {string} crmCompanyId - The CRM company ID
 * @returns {Promise<Object>} List of the company's contacts
 */
export const listContacts = (crmCompanyId) =>
	fetcher(`/api/v1/crm/contacts?crmCompanyId=${encodeURIComponent(crmCompanyId)}`);

/**
 * Creates a new CRM contact.
 * @param {Object} payload - The contact data
 * @returns {Promise<Object>} The created contact
 */
export const createContact = (payload) => fetcher('/api/v1/crm/contacts', { method: 'POST', body: payload });

/**
 * Updates an existing CRM contact.
 * @param {string} id - The contact ID
 * @param {Object} payload - The fields to update
 * @returns {Promise<Object>} The updated contact
 */
export const updateContact = (id, payload) => fetcher(`/api/v1/crm/contacts/${id}`, { method: 'PATCH', body: payload });

/**
 * Deletes a CRM contact.
 * @param {string} id - The contact ID
 * @returns {Promise<null>} No content on success
 */
export const deleteContact = (id) => fetcher(`/api/v1/crm/contacts/${id}`, { method: 'DELETE' });

// Channels

/**
 * Fetches all available channel types.
 * @param {boolean} [includeInactive=false] - Whether to include inactive channel types
 * @returns {Promise<Object>} List of channel types
 */
export const listChannelTypes = (includeInactive = false) =>
	fetcher(`/api/v1/crm/channels/types${includeInactive ? '?includeInactive=true' : ''}`);

/**
 * Fetches all channel configurations of the organization.
 * @returns {Promise<Object>} List of channel configurations
 */
export const listChannelConfigs = () => fetcher('/api/v1/crm/channels/configs');

/**
 * Creates or updates a channel configuration.
 * @param {Object} payload - The channel configuration data
 * @returns {Promise<Object>} The created or updated configuration
 */
export const upsertChannelConfig = (payload) =>
	fetcher('/api/v1/crm/channels/configs', { method: 'PUT', body: payload });

// Batch Deal Creation

/**
 * Batch-create deals from companies — either lead companies of a search query
 * (prospectQueryId + companyIds, or prospectQueryId + selectAll + filters?) or companies
 * already in the CRM (crmCompanyIds). Pass exactly one source.
 * @param {Object} payload - { prospectQueryId?, companyIds?|selectAll?|crmCompanyIds?, filters?, pipelineId, stageId, userId?, backfillEmails?, skipDuplicates? }
 * @returns {Promise<Object>} The creation result with summary
 */
export const createDealsFromCompanies = (payload) =>
	fetcher('/api/v1/crm/deals/create-from-companies', { method: 'POST', body: payload });

/**
 * Batch-create deals from contacts — CRM contacts, global prospect contacts (auto-materialised
 * into the CRM first) or a contact-list table selection. Pass exactly one source.
 * @param {Object} payload - { crmContactIds?|contactIds?|scope?, pipelineId, stageId, skipDuplicates? }
 *   where scope = { table: 'contactListContacts', contextId: listId, ids? | selectAll + filters? }
 * @returns {Promise<Object>} The creation result with summary
 */
export const createDealsFromCrmContacts = (payload) =>
	fetcher('/api/v1/crm/deals/create-from-crm-contacts', { method: 'POST', body: payload });

/**
 * Sync past email sends as activities for a specific deal.
 * @param {string} dealId - The deal ID
 * @param {Object} [payload={}] - Optional sync parameters (sinceIso)
 * @returns {Promise<Object>} The sync result
 */
export const syncEmailActivitiesForDeal = (dealId, payload = {}) =>
	fetcher(`/api/v1/crm/deals/${dealId}/sync-email-activities`, { method: 'POST', body: payload });

// Pipeline AI Generator
// analyze + status are ungated (used by both the CRM area and the first-run
// onboarding). generate creates a pipeline and requires crm.access.

/**
 * Analyzes a company via AI for pipeline generation.
 * @param {Object} payload - The analysis data (e.g. company name, website)
 * @returns {Promise<Object>} The analysis result
 */
export const analyzeCompany = (payload) =>
	fetcher('/api/v1/crm/pipeline-ai-generator/analyze', { method: 'POST', body: payload });

/**
 * Generates a pipeline via AI based on the company analysis. Requires crm.access.
 * @param {Object} payload - The generation data
 * @returns {Promise<Object>} The generated pipeline configuration
 */
export const generatePipeline = (payload) =>
	fetcher('/api/v1/crm/pipeline-ai-generator/generate', { method: 'POST', body: payload });

/**
 * Fetches the status of an ongoing generation. No crm.access required.
 * @param {string} generationId - The generation ID
 * @returns {Promise<Object>} The current generation status
 */
export const getPipelineGenerationStatus = (generationId) =>
	fetcher(`/api/v1/crm/pipeline-ai-generator/status/${generationId}`);

// Onboarding (first-run wizard, ungated — runs without crm.access)

/**
 * Generates the sales strategy with associated leads for onboarding.
 * Does not create a CRM pipeline — that requires the CRM add-on.
 * @param {Object} payload - The generation data
 * @returns {Promise<Object>} The strategy and lead generation result
 */
export const generatePipelineWithLeads = (payload) =>
	fetcher('/api/v1/onboarding/generate-with-leads', { method: 'POST', body: payload });

/**
 * Fetches the generated onboarding leads.
 * @param {string} generationId - The generation ID
 * @returns {Promise<Object>} The onboarding leads
 */
export const getOnboardingLeads = (generationId) => fetcher(`/api/v1/onboarding/onboarding-leads/${generationId}`);

// Insights

/**
 * Fetches AI-generated suggestions with optional filters.
 * @param {Object} [filters={}] - Optional filter parameters
 * @returns {Promise<Object>} List of suggestions
 */
export const listSuggestions = (filters = {}) => {
	const qs = new URLSearchParams(
		Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== ''),
	).toString();
	return fetcher(`/api/v1/crm/insights/suggestions${qs ? `?${qs}` : ''}`);
};

/**
 * Fetches the AI-generated daily plan.
 * @returns {Promise<Object>} The current daily plan
 */
export const getDailyPlan = () => fetcher('/api/v1/crm/insights/daily-plan');

/**
 * Regenerates the daily plan.
 * @returns {Promise<Object>} The newly generated daily plan
 */
export const regenerateDailyPlan = () => fetcher('/api/v1/crm/insights/daily-plan/regenerate', { method: 'POST' });

/**
 * Generates the next recommended action for a deal via AI.
 * @param {string} dealId - The deal ID
 * @returns {Promise<Object>} The generated next action
 */
export const generateNextAction = (dealId) => fetcher(`/api/v1/crm/insights/next-action/${dealId}`, { method: 'POST' });

/**
 * Accepts an AI suggestion and executes it.
 * @param {string} suggestionId - The suggestion ID
 * @returns {Promise<Object>} The execution result
 */
export const acceptSuggestion = (suggestionId) =>
	fetcher(`/api/v1/crm/insights/suggestions/${suggestionId}/accept`, { method: 'POST' });

/**
 * Dismisses an AI suggestion.
 * @param {string} suggestionId - The suggestion ID
 * @returns {Promise<Object>} The dismissal confirmation
 */
export const dismissSuggestion = (suggestionId) =>
	fetcher(`/api/v1/crm/insights/suggestions/${suggestionId}/dismiss`, { method: 'POST' });

// Companies

/**
 * Fetches all CRM companies with optional filters.
 * @param {Object} [filters={}] - Optional filter parameters
 * @returns {Promise<Object>} List of companies
 */
export const listCompanies = (filters = {}) => {
	const qs = new URLSearchParams(
		Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== ''),
	).toString();
	return fetcher(`/api/v1/crm/companies${qs ? `?${qs}` : ''}`);
};

/**
 * Creates a new CRM company.
 * @param {Object} payload - The company data
 * @returns {Promise<Object>} The created company
 */
export const createCompany = (payload) => fetcher('/api/v1/crm/companies', { method: 'POST', body: payload });

/**
 * Fetches the detail view of a CRM company.
 * @param {string} id - The company ID
 * @returns {Promise<Object>} The company with all details
 */
export const getCompanyDetail = (id) => fetcher(`/api/v1/crm/companies/${id}`);

/**
 * Updates an existing CRM company.
 * @param {string} id - The company ID
 * @param {Object} payload - The fields to update
 * @returns {Promise<Object>} The updated company
 */
export const updateCompany = (id, payload) =>
	fetcher(`/api/v1/crm/companies/${id}`, { method: 'PATCH', body: payload });

/**
 * Deletes a CRM company (soft delete).
 * @param {string} id - The company ID
 * @returns {Promise<null>} No content on success
 */
export const softDeleteCompany = (id) => fetcher(`/api/v1/crm/companies/${id}`, { method: 'DELETE' });

/**
 * Restores a soft-deleted CRM company.
 * @param {string} id - The company ID
 * @returns {Promise<Object>} The restored company
 */
export const restoreCompany = (id) => fetcher(`/api/v1/crm/companies/${id}/restore`, { method: 'POST' });

/**
 * Fetches all soft-deleted CRM companies (trash).
 * @param {Object} [filters={}] - Optional filter parameters
 * @returns {Promise<Object>} List of soft-deleted companies
 */
export const listTrash = (filters = {}) => {
	const qs = new URLSearchParams(
		Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== ''),
	).toString();
	return fetcher(`/api/v1/crm/companies/trash${qs ? `?${qs}` : ''}`);
};

/**
 * Soft-deletes several CRM companies in one request (admin/owner only).
 *
 * @param {{ids: string[]}|{selectAll: true, filters?: {search?: string, tagIds?: string[]}}} scope
 * @returns {Promise<{deleted: number}>}
 */
export const bulkDeleteCompanies = (scope) =>
	fetcher('/api/v1/crm/companies/bulk-delete', { method: 'POST', body: scope });

/**
 * Restores several soft-deleted CRM companies in one request (admin/owner only).
 *
 * @param {{ids: string[]}|{selectAll: true, filters?: {search?: string}}} scope
 * @returns {Promise<{restored: number}>}
 */
export const bulkRestoreCompanies = (scope) =>
	fetcher('/api/v1/crm/companies/trash/restore', { method: 'POST', body: scope });

// Custom Fields

/**
 * Fetches all custom field definitions.
 * @param {string} [entityType] - Optional entity type filter (e.g. 'deal', 'company')
 * @returns {Promise<Object>} List of field definitions
 */
export const listCustomFieldDefs = (entityType) =>
	fetcher(`/api/v1/crm/custom-fields${entityType ? `?entityType=${entityType}` : ''}`);

/**
 * Creates a new custom field definition.
 * @param {Object} payload - The field definition data
 * @returns {Promise<Object>} The created field definition
 */
export const createCustomFieldDef = (payload) =>
	fetcher('/api/v1/crm/custom-fields', { method: 'POST', body: payload });

/**
 * Updates an existing custom field definition.
 * @param {string} id - The field definition ID
 * @param {Object} payload - The fields to update
 * @returns {Promise<Object>} The updated field definition
 */
export const updateCustomFieldDef = (id, payload) =>
	fetcher(`/api/v1/crm/custom-fields/${id}`, { method: 'PATCH', body: payload });

/**
 * Deletes a custom field definition.
 * @param {string} id - The field definition ID
 * @returns {Promise<null>} No content on success
 */
export const deleteCustomFieldDef = (id) => fetcher(`/api/v1/crm/custom-fields/${id}`, { method: 'DELETE' });

/**
 * Reorders custom field definitions.
 * @param {Object} payload - The ordering data with IDs and positions
 * @returns {Promise<Object>} The updated order
 */
export const reorderFieldDefs = (payload) =>
	fetcher('/api/v1/crm/custom-fields/reorder', { method: 'POST', body: payload });

// Search

/**
 * Searches the CRM for deals, companies, and contacts.
 * @param {Object} [params] - The search parameters
 * @param {string} params.q - The search term
 * @param {string} [params.type] - Optional type filter (e.g. 'deal', 'company', 'contact')
 * @param {number} [params.page=1] - The page number
 * @param {number} [params.perPage=20] - Number of results per page
 * @returns {Promise<Object>} The paginated search results
 */
export const crmSearch = ({ q, type, page = 1, perPage = 20 } = {}) => {
	const params = new URLSearchParams({ q, page, perPage });
	if (type) params.set('type', type);
	return fetcher(`/api/v1/crm/search?${params}`);
};

// Products

/**
 * Fetches all products with optional filters.
 * @param {Object} [filters={}] - Optional filter parameters
 * @returns {Promise<Object>} List of products
 */
export const listProducts = (filters = {}) => {
	const qs = new URLSearchParams(
		Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== ''),
	).toString();
	return fetcher(`/api/v1/crm/products${qs ? `?${qs}` : ''}`);
};

/**
 * Creates a new product.
 * @param {Object} payload - The product data
 * @returns {Promise<Object>} The created product
 */
export const createProduct = (payload) => fetcher('/api/v1/crm/products', { method: 'POST', body: payload });

/**
 * Updates an existing product.
 * @param {string} id - The product ID
 * @param {Object} payload - The fields to update
 * @returns {Promise<Object>} The updated product
 */
export const updateProduct = (id, payload) => fetcher(`/api/v1/crm/products/${id}`, { method: 'PATCH', body: payload });

/**
 * Deletes a product.
 * @param {string} id - The product ID
 * @returns {Promise<null>} No content on success
 */
export const deleteProduct = (id) => fetcher(`/api/v1/crm/products/${id}`, { method: 'DELETE' });

/**
 * Fetches all deals associated with a product.
 * @param {string} id - The product ID
 * @returns {Promise<Object>} List of associated deals
 */
export const listProductDeals = (id) => fetcher(`/api/v1/crm/products/${id}/deals`);

// Product Bundles

/**
 * Fetches all product bundles with optional filters.
 * @param {Object} [filters={}] - Optional filter parameters
 * @returns {Promise<Object>} List of product bundles
 */
export const listBundles = (filters = {}) => {
	const qs = new URLSearchParams(
		Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== ''),
	).toString();
	return fetcher(`/api/v1/crm/products/bundles${qs ? `?${qs}` : ''}`);
};

/**
 * Creates a new product bundle.
 * @param {Object} payload - The bundle data
 * @returns {Promise<Object>} The created product bundle
 */
export const createBundle = (payload) => fetcher('/api/v1/crm/products/bundles', { method: 'POST', body: payload });

/**
 * Updates an existing product bundle.
 * @param {string} id - The bundle ID
 * @param {Object} payload - The fields to update
 * @returns {Promise<Object>} The updated product bundle
 */
export const updateBundle = (id, payload) =>
	fetcher(`/api/v1/crm/products/bundles/${id}`, { method: 'PATCH', body: payload });

/**
 * Deletes a product bundle.
 * @param {string} id - The bundle ID
 * @returns {Promise<null>} No content on success
 */
export const deleteBundle = (id) => fetcher(`/api/v1/crm/products/bundles/${id}`, { method: 'DELETE' });

/**
 * Fetches all deals associated with a product bundle.
 * @param {string} id - The bundle ID
 * @returns {Promise<Object>} List of associated deals
 */
export const listBundleDeals = (id) => fetcher(`/api/v1/crm/products/bundles/${id}/deals`);

// Integrations

/**
 * Fetches all available CRM integrations.
 * @returns {Promise<Object>} List of integrations with status
 */
export const listIntegrations = () => fetcher('/api/v1/crm/integrations');

/**
 * Disconnects an existing integration.
 * @param {string} slug - The integration slug (e.g. 'aircall')
 * @returns {Promise<null>} No content on success
 */
export const disconnectIntegration = (slug) =>
	fetcher(`/api/v1/crm/integrations/${slug}/disconnect`, { method: 'DELETE' });

// Aircall

/**
 * Starts the OAuth flow for the Aircall integration.
 * @returns {Promise<Object>} The OAuth URL and further installation data
 */
export const startAircallOAuth = () => fetcher('/api/v1/crm/integrations/aircall/install', { method: 'POST' });

/**
 * Updates the Aircall integration settings.
 * @param {Object} settings - The new settings
 * @returns {Promise<Object>} The updated settings
 */
export const updateAircallSettings = (settings) =>
	fetcher('/api/v1/crm/integrations/aircall/settings', { method: 'PUT', body: settings });

/**
 * Initiates a call via Aircall.
 * @param {string} phoneNumber - The phone number to dial
 * @returns {Promise<Object>} The call data
 */
export const aircallDial = (phoneNumber) =>
	fetcher('/api/v1/crm/integrations/aircall/call', { method: 'POST', body: { phoneNumber } });

/**
 * Resolves a phone number to matching CRM contacts and companies (caller identification).
 * Returns empty arrays when nothing matches — a call from an unknown number is not an error.
 *
 * @param {string} phoneNumber - The external party's number, in any format
 * @returns {Promise<{
 *   contacts: Array<{id: string, firstName: string|null, lastName: string|null, companyRole: string|null, crmCompanyId: string|null, companyName: string|null}>,
 *   companies: Array<{id: string, companyName: string|null}>
 * }>}
 */
export const lookupPhoneNumber = (phoneNumber) =>
	fetcher(`/api/v1/crm/integrations/aircall/phone-lookup?phoneNumber=${encodeURIComponent(phoneNumber)}`);

/**
 * Fetches all Aircall users.
 * @returns {Promise<Object>} List of Aircall users
 */
export const getAircallUsers = () => fetcher('/api/v1/crm/integrations/aircall/aircall-users');

/**
 * Fetches the mappings between Aircall and CRM users.
 * @returns {Promise<Object>} List of user mappings
 */
export const getAircallUserMappings = () => fetcher('/api/v1/crm/integrations/aircall/user-mappings');

/**
 * Updates the mappings between Aircall and CRM users.
 * @param {Object[]} mappings - Array of user mappings
 * @returns {Promise<Object>} The updated mappings
 */
export const updateAircallUserMappings = (mappings) =>
	fetcher('/api/v1/crm/integrations/aircall/user-mappings', { method: 'PUT', body: { mappings } });

// Calendar Integration

/**
 * Starts the OAuth flow for a calendar integration.
 * @param {string} provider - The calendar provider (e.g. 'google', 'microsoft')
 * @returns {Promise<Object>} The OAuth URL and further installation data
 */
export const startCalendarOAuth = (provider) =>
	fetcher(`/api/v1/crm/integrations/calendar/${provider}/install`, { method: 'POST' });

/**
 * Fetches all calendar connections.
 * @returns {Promise<Object>} List of calendar connections
 */
export const getCalendarConnections = () => fetcher('/api/v1/crm/integrations/calendar/connections');

/**
 * Updates the settings of a calendar connection.
 * @param {string} id - The connection ID
 * @param {Object} settings - The new settings
 * @returns {Promise<Object>} The updated settings
 */
export const updateCalendarConnection = (id, settings) =>
	fetcher(`/api/v1/crm/integrations/calendar/connections/${id}/settings`, { method: 'PUT', body: settings });

/**
 * Disconnects a calendar connection.
 * @param {string} id - The connection ID
 * @returns {Promise<null>} No content on success
 */
export const disconnectCalendar = (id) =>
	fetcher(`/api/v1/crm/integrations/calendar/connections/${id}`, { method: 'DELETE' });

/**
 * Fetches calendar events for a time range.
 * @param {Object} params - The time range parameters
 * @param {string} params.start - Start date (ISO string)
 * @param {string} params.end - End date (ISO string)
 * @returns {Promise<Object>} List of calendar events
 */
export const fetchCalendarEvents = ({ start, end }) =>
	fetcher(
		`/api/v1/crm/integrations/calendar/events?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
	);

/**
 * Creates a new calendar event.
 * @param {Object} payload - The event data
 * @returns {Promise<Object>} The created calendar event
 */
export const createCalendarEvent = (payload) =>
	fetcher('/api/v1/crm/integrations/calendar/events', { method: 'POST', body: payload });

/**
 * Deletes a calendar event.
 * @param {Object} payload - The identification data of the event to delete
 * @returns {Promise<null>} No content on success
 */
export const deleteCalendarEvent = (payload) =>
	fetcher('/api/v1/crm/integrations/calendar/events', { method: 'DELETE', body: payload });

// Tags

/**
 * Fetches all company tags of the organization.
 * @param {Object} [filters={}] - Optional filters (e.g. { withUsage: true })
 * @returns {Promise<{ tags: Array<{ id: string, name: string, usageCount?: number }> }>}
 */
export const listTags = (filters = {}) => {
	const qs = new URLSearchParams(
		Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== ''),
	).toString();
	return fetcher(`/api/v1/crm/tags${qs ? `?${qs}` : ''}`);
};

/**
 * Creates a new company tag.
 * @param {Object} payload - The tag data ({ name })
 * @returns {Promise<{ tag: { id: string, name: string } }>}
 */
export const createTag = (payload) => fetcher('/api/v1/crm/tags', { method: 'POST', body: payload });

/**
 * Renames an existing tag.
 * @param {string} id - The tag ID
 * @param {Object} payload - The fields to update ({ name })
 * @returns {Promise<{ tag: { id: string, name: string } }>}
 */
export const renameTag = (id, payload) => fetcher(`/api/v1/crm/tags/${id}`, { method: 'PATCH', body: payload });

/**
 * Deletes a tag definition (removes it from all companies).
 * @param {string} id - The tag ID
 * @returns {Promise<{ id: string }>}
 */
export const deleteTag = (id) => fetcher(`/api/v1/crm/tags/${id}`, { method: 'DELETE' });

/**
 * Fetches the tags assigned to a company.
 * @param {string} companyId - The CRM company ID
 * @returns {Promise<{ tags: Array<{ id: string, name: string }> }>}
 */
export const getCompanyTags = (companyId) => fetcher(`/api/v1/crm/companies/${companyId}/tags`);

/**
 * Replaces a company's full tag set.
 * @param {string} companyId - The CRM company ID
 * @param {string[]} tagIds - The desired tag ids ([] clears all)
 * @returns {Promise<{ tags: Array<{ id: string, name: string }> }>}
 */
export const setCompanyTags = (companyId, tagIds) =>
	fetcher(`/api/v1/crm/companies/${companyId}/tags`, { method: 'PUT', body: { tagIds } });

/**
 * Bulk-assigns tags to companies (additive — existing tags are kept).
 * @param {Object} payload - `{ tagIds, companyIds }` or `{ tagIds, selectAll: true, filters?: { search?, tagIds? } }`
 * @returns {Promise<{ linked: number }>}
 */
export const assignTags = (payload) => fetcher('/api/v1/crm/tags/assign', { method: 'POST', body: payload });

/**
 * Bulk-removes tags from companies.
 * @param {Object} payload - `{ tagIds, companyIds }` or `{ tagIds, selectAll: true, filters?: { search?, tagIds? } }`
 * @returns {Promise<{ unlinked: number }>}
 */
export const unassignTags = (payload) => fetcher('/api/v1/crm/tags/unassign', { method: 'POST', body: payload });
